import JSZip from 'jszip'

// Import the meaningful filename generator from the Create common components
import { generateMeaningfulFileName } from '../components/Create/common/ViewerControls'

/**
 * Downloads a single file from a given URL
 * @param {string} link - The URL of the file to download
 * @param {Object} options - Optional parameters for meaningful naming
 * @param {string} options.prompt - The prompt used to generate the content
 * @param {string} options.mode - The creation mode (image, video, music, 3d)
 * @param {string} options.title - Alternative title if available
 * @param {number} options.index - File index if multiple files
 * @param {number} options.total - Total number of files
 */
export const downloadFile = (link, options = {}) => {
  if (!link) {
    console.error('No link provided for download')
    return
  }

  fetch(link, {
    method: 'GET',
    headers: {},
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.arrayBuffer()
    })
    .then((buffer) => {
      const extension = link.split('.').pop() || 'png'

      // Determine file type from extension
      let fileType = 'content'
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension.toLowerCase())) {
        fileType = 'image'
      } else if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(extension.toLowerCase())) {
        fileType = 'video'
      } else if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(extension.toLowerCase())) {
        fileType = 'music'
      } else if (['glb', 'gltf', 'obj', 'fbx', 'ply', 'dae'].includes(extension.toLowerCase())) {
        fileType = '3d'
      }

      // Generate meaningful filename
      const baseName = generateMeaningfulFileName(fileType, options)
      const finalFilename = `${baseName}.${extension}`

      const url = window.URL.createObjectURL(new Blob([buffer]))
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.setAttribute('download', finalFilename)
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      // Clean up the object URL to free memory
      window.URL.revokeObjectURL(url)
    })
    .catch((err) => {
      console.error('Error downloading file:', err)
    })
}

/**
 * Downloads multiple files as a ZIP archive
 * @param {Array<string|Object>} urls - Array of URLs (strings) or objects with url property
 * @param {Function|null} setDownloading - Optional callback to set downloading state
 * @param {Object} options - Optional parameters for meaningful naming
 * @param {string} options.prompt - The prompt used to generate the content
 * @param {string} options.mode - The creation mode (image, video, music, 3d)
 * @param {string} options.title - Alternative title if available
 */
export const downloadFilesAsZip = async (urls, setDownloading = null, options = {}) => {
  if (!urls || urls.length === 0) {
    console.warn('No URLs provided for zip download')
    throw new Error('No files to download')
  }

  // Validate JSZip is available
  if (!JSZip) {
    console.error('JSZip library is not available')
    throw new Error('Download functionality is not available')
  }

  const zip = new JSZip()

  console.log('Starting zip download with URLs:', urls)

  setDownloading && setDownloading(true)

  try {
    let fileCount = 0
    const fileNameTracker = new Map() // Track filenames to avoid duplicates
    const failedDownloads = []

    for (let i = 0; i < urls.length; i++) {
      // Handle both URL strings and objects with url property
      const url = typeof urls[i] === 'string' ? urls[i] : urls[i]?.url

      if (!url || typeof url !== 'string') {
        console.warn(`Invalid URL at index ${i}:`, urls[i])
        failedDownloads.push(`Invalid URL at position ${i + 1}`)
        continue
      }

      try {
        console.log(`Fetching file ${i + 1}/${urls.length}: ${url}`)

        let blob = null

        // Try to fetch the file with CORS
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              Accept: '*/*',
            },
            mode: 'cors',
          })

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          blob = await response.blob()
        } catch (corsError) {
          // Only for images, try canvas approach as fallback
          if (url.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) {
            console.log(`CORS failed for image ${url}, trying canvas approach`)
            blob = await fetchImageAsCanvas(url)
          } else {
            throw corsError
          }
        }

        if (blob.size === 0) {
          console.warn(`Empty file received from ${url}`)
          failedDownloads.push(`Empty file at position ${i + 1}`)
          continue
        }

        // Generate meaningful filename instead of extracting from URL
        let baseFilename
        if (options.prompt || options.title) {
          // Determine file type from extension
          let fileType = 'content'
          const extension = url.split('.').pop()?.toLowerCase() || 'bin'
          if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
            fileType = 'image'
          } else if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(extension)) {
            fileType = 'video'
          } else if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(extension)) {
            fileType = 'music'
          } else if (['glb', 'gltf', 'obj', 'fbx', 'ply', 'dae'].includes(extension)) {
            fileType = '3d'
          }

          const meaningfulName = generateMeaningfulFileName(fileType, {
            ...options,
            index: i + 1,
            total: urls.length,
          })
          baseFilename = `${meaningfulName}.${extension}`
        } else {
          // Fallback to URL extraction
          const urlPath = url.split('/')
          const urlWithParams = urlPath[urlPath.length - 1]
          baseFilename = urlWithParams.split('?')[0] || `file_${i + 1}`

          // Ensure filename has an extension
          if (!baseFilename.includes('.')) {
            const mimeType = blob.type
            let extension = getExtensionFromMimeType(mimeType) || getExtensionFromUrl(url) || 'bin'
            baseFilename = `${baseFilename}.${extension}`
          }
        }

        // Handle duplicate filenames
        let filename = baseFilename
        let counter = 1
        while (fileNameTracker.has(filename)) {
          const nameWithoutExt = baseFilename.substring(0, baseFilename.lastIndexOf('.'))
          const ext = baseFilename.substring(baseFilename.lastIndexOf('.'))
          filename = `${nameWithoutExt}_${counter}${ext}`
          counter++
        }

        fileNameTracker.set(filename, true)
        zip.file(filename, blob)
        fileCount++

        console.log(`Added file to zip: ${filename} (${blob.size} bytes)`)
      } catch (fetchError) {
        console.error(`Error fetching file ${url}:`, fetchError)
        failedDownloads.push(`Download failed for file ${i + 1}`)
        continue
      }
    }

    if (fileCount === 0) {
      const errorMessage =
        failedDownloads.length > 0
          ? `All downloads failed. This may be due to CORS restrictions.`
          : 'No files were successfully added to the zip'
      console.error(errorMessage)
      throw new Error(errorMessage)
    }

    // Log partial failures
    if (failedDownloads.length > 0) {
      console.warn(`Some files failed to download: ${failedDownloads.join(', ')}`)
    }

    console.log(`Generating zip with ${fileCount} files...`)

    const zipData = await zip.generateAsync({
      type: 'blob',
      streamFiles: true,
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6,
      },
    })

    if (zipData.size === 0) {
      console.error('Generated zip file is empty')
      throw new Error('Generated zip file is empty')
    }

    console.log(`Zip generated successfully (${zipData.size} bytes)`)

    // Create download link
    const link = document.createElement('a')
    const objectUrl = window.URL.createObjectURL(zipData)
    link.href = objectUrl
    link.download = `Results_${new Date().toISOString().slice(0, 10)}.zip`

    // Trigger download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the object URL after a delay to ensure download completes
    setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl)
    }, 1000)

    console.log('Zip download initiated successfully')

    // Return summary for user feedback
    return {
      success: true,
      filesDownloaded: fileCount,
      totalFiles: urls.length,
      failures: failedDownloads,
    }
  } catch (error) {
    console.error('Error creating zip file:', error)
    throw error
  } finally {
    setDownloading && setDownloading(false)
  }
}

/**
 * Fetch image using canvas (for CORS fallback on images only)
 */
const fetchImageAsCanvas = async (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        canvas.toBlob(
          (blob) => {
            if (blob && blob.size > 0) {
              resolve(blob)
            } else {
              reject(new Error('Failed to convert image to blob'))
            }
          },
          'image/png',
          0.9
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`))
    }

    img.src = url
  })
}

/**
 * Get file extension from MIME type
 */
const getExtensionFromMimeType = (mimeType) => {
  if (!mimeType) return null

  const mimeMap = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'video/mp4': 'mp4',
    'video/mpeg': 'mpeg',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',
    'text/plain': 'txt',
    'text/html': 'html',
    'application/pdf': 'pdf',
    'application/json': 'json',
  }

  return mimeMap[mimeType.toLowerCase()]
}

/**
 * Get file extension from URL
 */
const getExtensionFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname
    const extension = pathname.split('.').pop()
    return extension && extension.length <= 4 ? extension : null
  } catch {
    return null
  }
}

/**
 * Converts a data URI to a Blob object
 * @param {string} dataURI - The data URI to convert
 * @returns {Blob} The converted Blob object
 */
export const dataURIToBlob = (dataURI) => {
  const splitDataURI = dataURI.split(',')
  const byteString = splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1])
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0]

  const ia = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)

  return new Blob([ia], { type: mimeString })
}

/**
 * Converts a URL to a File object
 * @param {string} url - The URL to convert
 * @param {string} type - The type/name for the file
 * @param {Function} callback - Callback function to handle the converted file
 */
export const convertUrlToFile = async (url, type, callback) => {
  try {
    const response = await fetch(url, { mode: 'cors' })
    const extension = url.split('.').pop() || 'png'
    const fileName = `${type}.${extension}`
    const blob = await response.blob()
    const file = new File([blob], fileName, { type: blob.type })
    callback(file)
  } catch (err) {
    console.error(err)
  }
}
